import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios from '../axios';
import orginalAxios from 'axios';
import { GetTrackingId } from '../utils/network';

const baseURL =
	window.location.protocol === 'https:'
		? 'https://api.admiterepolitie.ro/api'
		: 'http://localhost:8080/api';

const baseQuery = fetchBaseQuery({
	baseUrl: baseURL,
	prepareHeaders: (headers, { getState }) => {
		const storage = localStorage.getItem('userData');
		if (storage) {
			const parsedData = JSON.parse(storage);
			headers.set('Authorization', `Bearer ${parsedData.access}`);
		}
		return headers;
	}
});

const baseQueryWithRefresh = async (args, api, extraOptions) => {
	let result = await baseQuery(args, api, extraOptions);

	if (result.error) {
		switch (result.error.status) {
			case 401:
				const userData = JSON.parse(localStorage.getItem('userData'));

				const res = await orginalAxios({
					method: 'get',
					url: `${baseURL}/refreshToken`,
					headers: { Authorization: 'Bearer ' + userData.refresh }
				});
				if (res?.data?.message?.Token) {
					userData.access = res.data.message.Token.access_token;
					userData.refresh = res.data.message.Token.refresh_token;
					localStorage.setItem('userData', JSON.stringify(userData));

					// Retry the original request after refreshing the token
					result = await baseQuery(args, api, extraOptions);
				}
				break;
			case 403:
				localStorage.clear();
				window.location.href = '/';
				return;
		}
	}

	return result;
};

// Define a service using a base URL and expected endpoints
export const api = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithRefresh,
	endpoints: (builder) => ({
		fetchExams: builder.query({
			query: (payload) => {
				const storage = JSON.parse(localStorage.getItem('userData'));
				const searchParamsObj = {
					...payload,
					userId: storage.userId
				};

				const searchParams = new URLSearchParams(searchParamsObj);
				return {
					url: `/exams?${searchParams.toString()}`,
					method: 'GET'
					// Add cancel token if needed for request management
				};
			},
			transformResponse: (response) => response.message
		}),
		createExamLog: builder.mutation({
			query: (log) => ({
				url: '/exams/logActivity',
				method: 'POST',
				body: log
			}),
			transformResponse: (response) => {
				return response.message;
			}
		}),
		createLectureSummary: builder.mutation({
			query: (lectureSummary) => ({
				url: '/lectures',
				method: 'POST',
				body: lectureSummary
			})
		}),
		fetchLectureSummary: builder.query({
			query: (params) => ({
				url: `/lectures/${params.id}`,
				method: 'GET'
			})
		}),
		updateLectureSummary: builder.mutation({
			query: ({ id, lecture }) => {
				console.log(id);
				console.log(lecture);
				const url = `/lectures/${id}`;

				return {
					url,
					method: 'PATCH',
					body: lecture
				};
			}
		}),
		fetchLectures: builder.query({
			query: (queryFilter) => ({
				url: `/lectures?subject=${queryFilter.subject}&page=${queryFilter.page}`,
				method: 'GET'
			})
		}),
		fetchGeneratedExam: builder.query({
			query: (payload) => {
				const storage = JSON.parse(localStorage.getItem('userData'));

				const data = { userId: storage.userId, ...payload };

				if (payload.subject !== '' && payload.subject !== -1) data.examSubject = payload.subject;
				if (payload.difficulty?.length) data.difficulty = payload.difficulty;
				if (payload.chapters?.length) data.chapters = payload.chapters;
				if (payload.subject === 7 && payload.subchapters?.length)
					data.subchapters = payload.subchapters;
				if (payload.subject === 7 && payload.laws?.length) data.laws = payload.laws;
				if (payload.type) data.type = payload.type;
				if (payload.questionsCount) data.questionsCount = payload.questionsCount;

				const searchParams = new URLSearchParams(data);

				return {
					url: `/exams/generate?${searchParams.toString()}`,
					method: 'GET'
				};
			},
			transformResponse: (response) => {
				// Format the response to include question indexing
				const questions = response.message?.Questions?.map((question, index) => ({
					...question,
					orderID: index + 1
				}));

				return {
					...response.message,
					Questions: questions,
					startTime: new Date().getTime()
				};
			},
			providesTags: (result) => [{ type: 'GeneratedExam', id: result?.examId }]
		}),
		fetchExam: builder.query({
			query: (payload) => {
				const storage = JSON.parse(localStorage.getItem('userData'));
				const url = `/exams/${payload.id}?`;
				console.log(payload);
				const data = {
					userId: storage.userId,
					isGenerated: payload.id === 0,
					...(payload.subject !== -1 && { examSubject: payload.subject }),
					...(payload.id === 0 && { questionIds: payload.questionIds })
				};
				console.log(data);
				const searchParams = new URLSearchParams(data);
				return {
					url: `${url}${searchParams.toString()}`,
					method: 'GET'
				};
			},
			transformResponse: (response) => {
				const data = response?.message;
				data.Questions = data.Questions.map((question, index) => ({
					...question,
					orderID: index + 1
				}));

				return data;
			}
		}),
		getUsers: builder.query({
			query: (userId) => `/users/${userId}`,
			transformResponse: (response) => response.message
		}),
		getNewsFeed: builder.query({
			query: (userId) => `/users/${userId}/news-feed`
		}),
		getReports: builder.query({
			query: (filters) => {
				const url = '/admin/reports/questions';

				const { currentPage } = filters;
				if (currentPage != null) {
					filters.page = currentPage;
				}

				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			},
			transformResponse: (response) => response.message
		}),
		getQuestionStatistics: builder.query({
			query: () => '/admin/statistics/questions',
			transformResponse: (response) => response.message
		}),
		getExamsStatistics: builder.query({
			query: () => '/admin/statistics/exams',
			transformResponse: (response) => response.message
		}),
		solveReport: builder.mutation({
			query: ({ id, notificationMessage, recipientId }) => {
				const url = `/admin/reports/questions/${id}`;
				const { userId } = JSON.parse(localStorage.getItem('userData'));

				const payload = {
					message: notificationMessage,
					recipientId: recipientId,
					userId: userId
				};

				return {
					url,
					method: 'POST',
					body: payload
				};
			}
		}),
		downloadReports: builder.query({
			query: () => ({
				url: '/admin/reports/questions/download',
				method: 'GET',
				responseHandler: async (response) => {
					const blob = await response.blob();
					const url = window.URL.createObjectURL(blob);

					const link = document.createElement('a');
					link.href = url;
					link.download = 'question_reports.csv'; // File name
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					// Free up the blob memory
					window.URL.revokeObjectURL(url);
				},
				cache: 'no-cache'
			})
		}),
		getQuestions: builder.query({
			query: (filters) => {
				const url = '/questions';

				const { currentPage } = filters;
				if (currentPage) {
					filters.page = currentPage;
				}

				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			},
			transformResponse: (response) => response.message
		}),
		getSimulation: builder.query({
			query: (filters) => {
				const { userId } = JSON.parse(localStorage.getItem('userData'));
				const url = `/users/${userId}/simulations`;
				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			}
			// providesTags: (result, error, id) => [{ type: 'Simulation', id: id }],
		}),
		getSimulationHistory: builder.query({
			query: (filters) => {
				const { userId } = JSON.parse(localStorage.getItem('userData'));
				const url = `/users/${userId}/simulations/history`;
				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			}
		}),
		importExam: builder.mutation({
			query: (examData) => {
				const { data, file } = examData;

				const formData = new FormData();
				formData.append('file', file);

				Object.keys(data).forEach((key) => {
					formData.append(key, data[key]);
				});

				return {
					url: '/exams/upload',
					method: 'POST',
					body: formData
				};
			}
		}),
		importQuestions: builder.mutation({
			query: (file) => {
				const formData = new FormData();
				formData.append('file', file);

				return {
					url: '/questions/upload',
					method: 'POST',
					body: formData
				};
			}
		}),
		getExams: builder.query({
			query: (filters) => {
				const url = '/exams';
				// const { currentPage } = filters;
				// if (currentPage) {
				// 	filters.page = currentPage;
				// }

				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			},
			transformResponse: (response) => response.message
		}),
		getUsersStatistics: builder.query({
			query: () => '/admin/statistics',
			transformResponse: (response) => response.message
		}),
		getDailyActiveUsers: builder.query({
			query: () => '/admin/users/dailyActive',
			transformResponse: (response) => response.message
		}),
		getMonthlyActiveUsers: builder.query({
			query: () => '/admin/users/monthlyActive',
			transformResponse: (response) => response.message
		}),
		getPlatformReports: builder.query({
			query: (currentPage) => {
				let url = '/admin/reports/platform';
				if (currentPage !== null) {
					url = `${url}?page=${currentPage}`;
				}
				return url;
			},
			transformResponse: (response) => response.message
		}),
		updatePlatformReport: builder.mutation({
			query: ({ id, status }) => {
				const url = `/admin/reports/platform/${id}`;
				const payload = {
					Status: status
				};
				return {
					url,
					method: 'PATCH',
					body: payload
				};
			},
			transformResponse: (response) => response.message
		}),
		updateQuestion: builder.mutation({
			query: ({ id, question }) => {
				const url = `/questions/${id}`;

				return {
					url,
					method: 'PATCH',
					body: question
				};
			},
			transformResponse: (response) => response.message
		}),
		updateExam: builder.mutation({
			query: ({ id, exam }) => {
				const url = `/exams/${id}`;

				return {
					url,
					method: 'PATCH',
					body: exam
				};
			}
		}),
		createSimulationHistory: builder.mutation({
			query: (payload) => {
				let userData;
				const storage = localStorage.getItem('userData');
				if (storage) {
					userData = JSON.parse(storage);
				}
				const url = `/users/${userData.userId}/simulations/history`;

				return {
					url: url,
					method: 'POST',
					body: payload
				};
			}
		}),
		getChapters: builder.query({
			query: (filters) => {
				const url = `/chapters`;
				const searchParams = new URLSearchParams(filters);
				return `${url}?${searchParams.toString()}`;
			},
			transformResponse: (response) => response.message
		}),
		markAsRead: builder.mutation({
			query: ({ userId, notificationId }) => ({
				url: `/users/${userId}/notifications/${notificationId}`,
				method: 'PATCH'
			}),
			transformResponse: (response) => response.message
		}),
		getNotifications: builder.query({
			query: ({ userId, page = 1 }) => {
				return {
					url: `/users/${userId}/notifications`,
					method: 'GET',
					params: { page }
				};
			},
			transformResponse: (response) => {
				// Adjust the response structure if necessary
				return {
					notifications: response?.Notifications,
					unopenedCount: response?.CountNotifications,
					total: response?.TotalNotification
				};
			},
			providesTags: ['Notifications'] // Use this for cache invalidation
		})
	}),
	tagTypes: ['Simulation', 'Notifications']
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetExamsStatisticsQuery,
	useGetReportsQuery,
	useGetQuestionsQuery,
	useGetChaptersQuery,
	useGetNewsFeedQuery,
	useFetchExamQuery,
	useLazyFetchExamQuery,
	useLazyFetchExamsQuery,
	useFetchGeneratedExamQuery,
	useCreateExamLogMutation,
	useCreateLectureSummaryMutation,
	useFetchLectureSummaryQuery,
	useFetchLecturesQuery,
	useLazyFetchLectureSummaryQuery,
	useUpdateLectureSummaryMutation,
	useLazyFetchGeneratedExamQuery,
	useGetExamsQuery,
	useLazyGetExamsQuery,
	useGetUsersQuery,
	useGetNotificationsQuery,
	useLazyGetNotificationsQuery,
	useLazyGetUsersQuery,
	useGetQuestionStatisticsQuery,
	useGetUsersStatisticsQuery,
	useGetDailyActiveUsersQuery,
	useGetMonthlyActiveUsersQuery,
	useSolveReportMutation,
	useLazyDownloadReportsQuery,
	useLazyGetSimulationQuery,
	useGetSimulationHistoryQuery,
	useLazyGetSimulationHistory,
	useCreateSimulationHistoryMutation,
	useImportExamMutation,
	useImportQuestionsMutation,
	useGetPlatformReportsQuery,
	useUpdatePlatformReportMutation,
	useUpdateQuestionMutation,
	useUpdateExamMutation,
	useMarkAsReadMutation
} = api;

export const isActiveUserSubscription = async () => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	const url = `/api/users/${userData.userId}/subscription`;
	try {
		const res = await axios.get(url, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 202) {
			return res.data.message;
		}
	} catch (e) {
		return false;
	}
};

export const getSubscription = async (subId, enqueueSnackbar) => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	const url = `/api/users/${userData.userId}/subscription/${subId}`;
	try {
		const res = await axios.get(url, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 202) {
			return res.data.message;
		} else {
			enqueueSnackbar('Eroare de server.', {
				variant: 'error'
			});
			throw new Error('API request failed');
		}
	} catch (e) {
		enqueueSnackbar(`Reînnoire abonament deja dezactivata`, {
			variant: 'error'
		});
		throw new Error('API request failed');
	}
};

export const cancelSubscription = async (subId, enqueueSnackbar) => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	const url = `/api/users/${userData.userId}/subscription/${subId}`;
	try {
		const res = await axios.delete(url, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 202) {
			enqueueSnackbar('Plata automată a fost dezactivată!', {
				variant: 'success'
			});
			return res.data.message;
		} else {
			enqueueSnackbar('Eroare de server.', {
				variant: 'error'
			});
			throw new Error('API request failed');
		}
	} catch (e) {
		enqueueSnackbar(`Reînnoire abonament deja dezactivata`, {
			variant: 'error'
		});
		throw new Error('API request failed');
	}
};

export const updateSubscription = async (payload, enqueueSnackbar, setLoading) => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	const url = `/api/users/${userData.userId}/subscription/${payload.subscriptionId}`;
	try {
		const res = await axios.patch(url, payload, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 202) {
			const checkoutURL = res.data?.message.CheckoutURL;
			if (checkoutURL) {
				window.open(checkoutURL, '_self');
				return;
			}
			setLoading(false);
			enqueueSnackbar('Abonament modificat cu succes.', {
				variant: 'success'
			});
			return res.data.message;
		} else {
			setLoading(false);
			enqueueSnackbar('Eroare de server.', {
				variant: 'error'
			});
			throw new Error('API request failed');
		}
	} catch (e) {
		setLoading(false);

		enqueueSnackbar(`API request failed`, {
			variant: 'error'
		});
		throw new Error('API request failed');
	}
};

export const createBillingSession = async (enqueueSnackbar) => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	try {
		const res = await axios.get(`api/users/${userData.userId}/billingPortal`);

		if (res.status === 202) {
			const checkoutURL = res.data.message;
			window.open(checkoutURL, '_self');
		}
	} catch (err) {
		switch (err.response.status) {
			case 400:
				enqueueSnackbar('Acest user nu exista!', {
					variant: 'error'
				});
				break;
			default:
				enqueueSnackbar('Eroare de server.', {
					variant: 'error'
				});
		}
	}
};

export const getUsersTickets = async () => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	const url = `/api/users/${userData.userId}/reports`;
	try {
		const res = await axios.get(url, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 200) {
			return res.data.message;
		}
	} catch (e) {
		return false;
	}
};

export const getUser = async () => {
	let userData;
	const storage = localStorage.getItem('userData');

	if (storage) {
		userData = JSON.parse(storage);
	}

	if (!userData?.userId) {
		return;
	}

	const url = `/api/users/${userData?.userId}`;
	try {
		const res = await axios.get(url, {
			headers: { Authorization: `Bearer ${userData.access}` }
		});

		if (res.status === 202) {
			return res.data.message;
		}
	} catch (e) {
		return false;
	}
};

export const createReportMessage = async (reportId, reportMessage) => {
	const { userId } = JSON.parse(localStorage.getItem('userData'));
	const url = `/api/users/${userId}/reports/${reportId}/messages`;

	const payload = {
		text: reportMessage
	};

	return await axios.post(url, payload);
};

export const getReportMessages = async (reportId) => {
	const { userId } = JSON.parse(localStorage.getItem('userData'));
	const url = `/api/users/${userId}/reports/${reportId}/messages`;

	return await axios.get(url);
};

export const getDashboard = async () => {
	const userData = JSON.parse(localStorage.getItem('userData'));

	let url;
	if (userData) {
		url = `/api/users/${userData.userId}/dashboard`;
	}
	if (!url) {
		return;
	}

	return await axios.get(url);
};

export const getExamLogs = async (page) => {
	const storage = JSON.parse(localStorage.getItem('userData'));

	const url = `/api/users/${storage.userId}/exam-logs?page=${page}`;

	return await axios.get(url);
};

export const fetchProducts = async () => {
	const url = '/api/products';

	try {
		const res = await axios.get(url);

		if (res.status === 202) {
			return res.data.message;
		}
	} catch (e) {
		return false;
	}
};

export const createCheckoutSession = async (
	productId,
	enqueueSnackbar,
	upgradeSubscription,
	setLoading,
	eventId
) => {
	const storage = JSON.parse(localStorage.getItem('userData'));

	const fbpValue = GetTrackingId('_fbp');
	const fbcValue = GetTrackingId('fbclid');
	const ttpValue = GetTrackingId('_ttp');
	const ttclidValue = GetTrackingId('ttclid');

	const payload = {
		productId: productId,
		eventId: eventId,
		fbp: fbpValue,
		fbc: fbcValue,
		ttclid: ttclidValue,
		ttp: ttpValue
	};

	let url = `api/users/${storage.userId}/checkout-session`;
	if (upgradeSubscription) {
		url += '?upgradeSubscription=' + upgradeSubscription;
	}
	try {
		const res = await axios.post(url, payload);

		if (res.status === 202) {
			const checkoutURL = res.data.message;
			window.open(checkoutURL, '_self');
		}
	} catch (err) {
		setLoading(false);
		switch (err.response.status) {
			case 400:
				enqueueSnackbar('Ai deja un abonament activ!', {
					variant: 'error'
				});
				break;
			default:
				enqueueSnackbar('Eroare de server.', {
					variant: 'error'
				});
		}
	}
};

export const createQuestionReport = async (questionId, reportTypes, textboxValue) => {
	const { userId } = JSON.parse(localStorage.getItem('userData'));
	const url = `/api/questions/${questionId}/report`;

	const payload = {
		reportTypes,
		userId,
		comments: textboxValue
	};

	return await axios.post(url, payload);
};
